import React, { FC, useCallback, useState, useMemo } from "react";
import { Button, IconButton, Tooltip, Switch } from "@xapp/components";
import { Paper } from "@mui/material";
import { SummarySubscription, SummaryFrequency } from "generated/graphql";
import { SummarySubscriptionEditor } from "./SummarySubscriptionEditor";
import { getAppName } from "../utils/formatters";
import { SubscribableApp } from "../types";

export interface SummarySubscriptionListProps {
    subscriptions: SummarySubscription[];
    subscribableApps: SubscribableApp[];
    onCreate: (subscription: Partial<SummarySubscription>) => void;
    onUpdate: (subscriptionId: string, subscription: Partial<SummarySubscription>) => void;
    onDelete: (subscriptionId: string) => void;
}

export const SummarySubscriptionList: FC<SummarySubscriptionListProps> = (props) => {
    const { subscriptions, subscribableApps, onCreate, onUpdate, onDelete } = props;

    const [editingSubscription, setEditingSubscription] = useState<SummarySubscription | null>(null);
    const [editorActive, setEditorActive] = useState(false);

    const handleAdd = useCallback(() => {
        setEditingSubscription(null);
        setEditorActive(true);
    }, []);

    const handleEdit = useCallback((subscription: SummarySubscription) => {
        setEditingSubscription(subscription);
        setEditorActive(true);
    }, []);

    const handleDelete = useCallback(
        (subscriptionId: string) => {
            if (confirm("Are you sure you want to delete this subscription?")) {
                onDelete(subscriptionId);
            }
        },
        [onDelete]
    );

    const handleToggleEnabled = useCallback(
        (subscriptionId: string, enabled: boolean) => {
            onUpdate(subscriptionId, { enabled });
        },
        [onUpdate]
    );

    const handleSave = useCallback(
        (subscription: Partial<SummarySubscription>) => {
            if (editingSubscription) {
                onUpdate(editingSubscription.subscriptionId, subscription);
            } else {
                onCreate(subscription);
            }
            setEditorActive(false);
            setEditingSubscription(null);
        },
        [editingSubscription, onCreate, onUpdate]
    );

    const handleDismiss = useCallback(() => {
        setEditorActive(false);
        setEditingSubscription(null);
    }, []);

    const getAppNameMemo = useMemo(() => (appId: string) => getAppName(appId, subscribableApps), [subscribableApps]);

    const renderSubscriptionsByFrequency = (frequency: SummaryFrequency, title: string) => {
        const frequencySubscriptions = subscriptions.filter((s) => s.frequency === frequency);

        return (
            <div style={{ marginBottom: "24px" }}>
                <h4>{title}</h4>
                {frequencySubscriptions.length === 0 ? (
                    <p>No subscriptions yet.</p>
                ) : (
                    frequencySubscriptions.map((sub) => (
                        <Paper key={sub.subscriptionId} sx={{ marginBottom: "12px", padding: "16px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ flex: 1 }}>
                                    <p><strong>Apps:</strong> {sub.appIds.map(getAppNameMemo).join(", ")}</p>
                                    <p><strong>Grouping:</strong> {sub.grouping === "individual" ? "One email per app" : "Combined email"}</p>
                                    <p><strong>Channel:</strong> {sub.channel}</p>
                                    {sub.lastSent && <p><strong>Last Sent:</strong> {new Date(sub.lastSent).toLocaleDateString()}</p>}
                                    {sub.nextScheduled && <p><strong>Next Scheduled:</strong> {new Date(sub.nextScheduled).toLocaleDateString()}</p>}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Switch
                                        checked={sub.enabled}
                                        onChange={(checked) => handleToggleEnabled(sub.subscriptionId, checked)}
                                    />
                                    <Tooltip content="Edit">
                                        <IconButton iconName="edit" onClick={() => handleEdit(sub)} />
                                    </Tooltip>
                                    <Tooltip content="Delete">
                                        <IconButton iconName="delete" onClick={() => handleDelete(sub.subscriptionId)} />
                                    </Tooltip>
                                </div>
                            </div>
                        </Paper>
                    ))
                )}
                <Button onClick={handleAdd}>Add {title}</Button>
            </div>
        );
    };

    return (
        <div>
            {renderSubscriptionsByFrequency(SummaryFrequency.Weekly, "Weekly Summaries")}
            {renderSubscriptionsByFrequency(SummaryFrequency.Monthly, "Monthly Summaries")}

            <SummarySubscriptionEditor
                subscription={editingSubscription}
                subscribableApps={subscribableApps}
                active={editorActive}
                onSave={handleSave}
                onDismiss={handleDismiss}
            />
        </div>
    );
};
